import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import fetchAppointmentById from '../api/fetchAppointmentById';
import { Patient as AppointmentPatient } from '../api/fetchAppointments';
import { addPatient, NewPatient } from '../store/actions/app';
import { initVisit } from '../store/actions/visit';
import { calculateAge } from '../store/utils';
import { GO_TO_PRESCRIPTION_PAD, GO_TO_ADD_NEW_PATIENT } from '../routes/paths';
import { Patient } from '../store/types/patient';
import { getItemFromMisc, getPrescriptionById, getVisitById, MISC_KEYS } from '../store/idbHandler';
import { NewPatientLocationState } from '../pages/NewPatient';
import { loadPrescription } from '../store/actions/prescriptionPad';
import AppLoader from '../components/common/AppLoader';
import { GenieSettings } from '../api/pushGenie';
import { GENDER_CODE_TO_NAME, getPatientNameText } from '../pages/AppointmentUtils';
import { track } from '../tracking';
import { getMyDbId } from '../api/mactiveid';

export const getPhoneNumber = (patient: AppointmentPatient): string | undefined => {
    const phoneNumber =
        patient.mobile && patient.mobile.length > 0
            ? patient.mobile.substr(patient.mobile.length - 10)
            : patient.mobile?.replace('+91', '');

    return phoneNumber;
};

const InitVisitFromId: React.FC = () => {
    const history = useHistory();
    const location = useLocation();

    const dispatch = useDispatch();

    const goToError = ({ msg, errorCode }: { msg?: string; errorCode?: string }): void => {
        track('ERROR', { on: 'INIT_VISIT_FROM_ID', what: msg, code: errorCode });

        alert(`${errorCode}: Could not start visit`);
        history.replace('/');
    };

    const proccedWithDeets = async ({
        vId,
        isVideoCall,
    }: {
        vId?: string | null;
        isVideoCall?: boolean | null;
    }) => {
        if (vId) {
            const localVisit = await getVisitById(vId);

            if (localVisit) {
                const p = await getPrescriptionById(localVisit.prescriptionId);
                dispatch(loadPrescription(p));
                history.replace(GO_TO_PRESCRIPTION_PAD(localVisit.prescriptionId, 'edit'));
                return;
            }

            fetchAppointmentById(vId)
                .then(async (res) => {
                    if (!res.data) {
                        goToError({ errorCode: 'V3J' });
                        return;
                    }
                    const a = res.data;
                    const { patient } = a;

                    const ptName = getPatientNameText(patient);
                    const ptPh = getPhoneNumber(patient);

                    if (!patient.gen || !ptName || !ptPh) {
                        let age;
                        if (patient.dob) {
                            const sp = patient.dob.split('-');
                            if (sp.length === 3) {
                                age = {
                                    dob: {
                                        dd: sp[2],
                                        mm: sp[1],
                                        yyyy: sp[0],
                                    },
                                };
                            }
                        }

                        const s: NewPatientLocationState = {
                            oid: a.patient_oid || undefined,
                            phoneNumber: getPhoneNumber(patient),
                            name: a.patient_oid ? getPatientNameText(patient) : undefined,
                            visitId: a.aid,
                            age: a.patient_oid ? age : undefined,
                            visitType: a['visit-type'],
                        };

                        history.push({
                            pathname: GO_TO_ADD_NEW_PATIENT(a.clinic_id),
                            state: s,
                        });

                        return;
                    }

                    let age;
                    if (patient.dob) {
                        const t = calculateAge(patient.dob);
                        age = { value: t.age, month: t.month, dob: patient.dob };
                    }

                    const newPatient: NewPatient = {
                        localId: a.patient_oid || undefined,
                        oid: a.patient_oid || undefined,
                        name: ptName,
                        phCode: patient.mobile?.startsWith('+1')
                            ? '+1'
                            : patient.mobile?.slice(0, 3),
                        phoneNumber: ptPh,
                        gender: GENDER_CODE_TO_NAME[patient.gen || ''] || '',
                        age,
                    };

                    const onPatientUpsertSuccess = (
                        p: Patient & { apiFailure: boolean; error?: any },
                    ) => {
                        const onSuccess = async (prescriptionId: string) => {
                            const p = await getPrescriptionById(prescriptionId);
                            dispatch(loadPrescription(p));
                            history.replace(GO_TO_PRESCRIPTION_PAD(prescriptionId, 'edit'));
                        };

                        const initVisitAction = initVisit(
                            // localId === oid === BE pattient id === pid
                            {
                                ...newPatient,
                                phCode: newPatient.phCode || '+91',
                                localId: p.localId,
                                id: p.id,
                                uuid: p.uuid,
                                abha: p.abha,
                                relations: p.relations,
                            },
                            a.doctor_oid,
                            a.clinic_id,
                            a.aid,
                            onSuccess,
                            undefined,
                            {
                                visitType: a['visit-type'],
                                saId: a.sa_id || undefined,
                                autoCopy: true,
                            },
                        );
                        initVisitAction && dispatch(initVisitAction);
                    };

                    const settings = await getItemFromMisc<GenieSettings>(
                        getMyDbId() || '',
                        MISC_KEYS.settings,
                    );

                    dispatch(
                        addPatient({
                            newPatient,
                            sendWelcome: settings.sendWelcome,
                            onSuccess: onPatientUpsertSuccess,
                            visitid: a.aid,
                        }),
                    );
                })
                .catch((e) => {
                    goToError({ errorCode: 'V2J' });
                    return;
                });
        } else {
            goToError({ errorCode: 'V9JK' });

            return;
        }
    };

    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const vId = urlParams.get('vid');
        const isVideoCall = urlParams.get('video') === 'true';

        try {
            proccedWithDeets({ vId, isVideoCall });
        } catch (e) {
            goToError({
                errorCode: 'V23K',
                msg: 'Something went wrong, please try again later',
            });
            return;
        }
    }, []);

    return (
        <div className="flex flex-col items-center justify-center w-screen h-screen space-y-64">
            <AppLoader />
        </div>
    );
};

export default InitVisitFromId;
